import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { getOrderDeliveryConfirmation } from '@/api/deliveries';
import { deleteOrderMedia } from '@/api/orders';
import { getDeliveryAction, updateDeliveryWorkspace, releaseDeliveryAction } from '@/utils/deliveryDraftStore';

const empty = { status: 'loading', items: [], message: '', selected: null };
const knownRejections = new Set([400, 401, 403, 404, 409, 422]);

// Metadata is independent of draft fields. An uncertain destructive action is
// retained in the existing principal/order workspace, not in component memory.
export function useSavedDeliveryMedia({ enabled, opening, owns, getWork, getToken, canAct, admit, onChanged }) {
  const [state, setState] = useState({ ...empty, opening });
  const current = useRef({ ...empty, opening });
  const generation = useRef(0);
  const controller = useRef(null);
  const replace = (next) => {
    current.current = { ...current.current, ...next, opening };
    setState(current.current);
  };
  const read = async ({ token, explicit = false, lease } = {}) => {
    if (!owns()) return false;
    controller.current?.abort();
    const request = ++generation.current;
    const abort = new AbortController();
    controller.current = abort;
    replace({ status: 'loading', selected: null });
    try {
      const result = await getOrderDeliveryConfirmation(opening.orderId, { token, signal: abort.signal });
      if (!owns() || request !== generation.current || abort.signal.aborted) return false;
      if (result?.order_id !== opening.orderId || result.status !== 'delivered'
        || !result.confirmation || typeof result.confirmation !== 'object'
        || Array.isArray(result.confirmation)
        || (result.confirmation.media_files != null && !Array.isArray(result.confirmation.media_files))) {
        throw new Error('Invalid delivery confirmation');
      }
      const recorded = result.confirmation.media_files || [];
      const items = recorded.filter((item) => item?.file_type !== 'signature').map((item, index) => {
        const path = item?.storage_path;
        const type = item?.file_type;
        const valid = typeof path === 'string' && !!path.trim() && path === path.trim()
          && ['photo', 'video'].includes(type);
        return { key: `${index}`, path: valid ? path : null, type, valid };
      });
      const counts = new Map();
      recorded.forEach((item) => {
        if (typeof item?.storage_path === 'string') {
          const path = item.storage_path.trim();
          counts.set(path, (counts.get(path) || 0) + 1);
        }
      });
      items.forEach((item) => { if (counts.get(item.path) > 1) { item.valid = false; item.path = null; } });
      const work = getWork();
      const removal = work?.savedMediaRemoval;
      let message = '';
      if (explicit && removal && removal.status !== 'pending') {
        if (recorded.some((item) => typeof item?.storage_path !== 'string' || !item.storage_path.trim()
          || item.storage_path !== item.storage_path.trim())) {
          throw new Error('Current saved media identities are incomplete');
        }
        const present = recorded.some((item) => item.storage_path === removal.path);
        if (present && !items.some((item) => item.valid && item.path === removal.path)) {
          throw new Error('Current saved media identity is ambiguous');
        }
        // Acknowledged absence is retained even if an obsolete read republishes
        // the old target. Never offer another delete of an acknowledged target.
        if (removal.status === 'acknowledged' && present) {
          replace({ status: 'error', items: items.filter((item) => item.path !== removal.path),
            message: 'Removal succeeded, but the refreshed list has not caught up. Retry refresh only.' });
          return false;
        }
        message = removal.status === 'acknowledged' ? 'Saved media removed. Delivery history has been updated.'
          : present ? 'Current delivery reviewed: the selected item is still present. You may explicitly choose removal again.'
            : 'Current delivery reviewed: the selected item is absent. This does not establish who removed it.';
        updateDeliveryWorkspace(work, { savedMediaRemoval: null }, lease);
      }
      replace({ status: 'ready', items, message, selected: null });
      return true;
    } catch {
      if (!owns() || request !== generation.current || abort.signal.aborted) return false;
      const removal = getWork()?.savedMediaRemoval;
      replace({ status: 'error', message: removal?.status === 'acknowledged'
        ? 'Removal succeeded, but refresh failed. Retry refresh only; do not remove it again.'
        : removal?.status === 'unknown' ? 'Removal outcome is unknown. Current delivery could not be loaded. Review it again before doing more work.'
          : 'Saved delivery media could not be loaded. This does not mean the delivery has no media.' });
      return false;
    }
  };
  useEffect(() => {
    replace(empty);
    if (enabled && owns()) {
      const token = getToken();
      if (token && owns()) void read({ token });
      else replace({ status: 'error', message: 'Sign in again to view saved delivery media.' });
    }
    return () => { controller.current?.abort(); };
    // One admitted opening owns all reads; changing parent order objects cannot
    // reinitialize the draft or silently resolve uncertain mutations.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, opening]);

  const refresh = async () => {
    if (!enabled || !canAct()) return;
    const admission = admit('review-saved-media');
    if (!admission) return;
    try {
      const loaded = await read({ token: admission.token, explicit: true, lease: admission.lease });
      if (loaded && owns()) {
        try { await onChanged?.(opening.orderId, { status: 'delivered' }); }
        catch { if (owns()) replace({ message: 'Saved media is current, but the run list could not refresh. Retry refresh only.' }); }
      }
    } finally { releaseDeliveryAction(admission.lease); }
  };
  const selectRemoval = (item) => {
    if (!enabled || opening.binding?.source !== 'cloud' || current.current.opening !== opening
      || !canAct() || getDeliveryAction(getWork()) || getWork()?.savedMediaRemoval
      || current.current.status !== 'ready' || !item.valid
      || !current.current.items.some((entry) => entry.path === item.path && entry.type === item.type)) return;
    replace({ selected: { path: item.path, type: item.type } });
  };
  const confirmRemoval = async () => {
    const target = current.current.selected;
    if (!target || opening.binding?.source !== 'cloud' || current.current.opening !== opening
      || !canAct() || getWork()?.savedMediaRemoval || current.current.status !== 'ready'
      || !current.current.items.some((item) => item.path === target.path && item.type === target.type)) return;
    const admission = admit('remove-saved-media');
    if (!admission) return;
    const { token, lease } = admission;
    let active = updateDeliveryWorkspace(admission.current, { savedMediaRemoval: { ...target, status: 'pending' } }, lease);
    if (!active) { releaseDeliveryAction(lease); return; }
    controller.current?.abort();
    ++generation.current;
    replace({ selected: null, message: 'Removing saved media…' });
    try {
      const result = await deleteOrderMedia(opening.orderId, target.path, 'delivery', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (result?.order_id !== opening.orderId || result.deleted !== target.path) throw new Error('Unverified removal response');
      active = updateDeliveryWorkspace(active, { savedMediaRemoval: { ...target, status: 'acknowledged' } }, lease) || active;
      if (!owns()) return;
      replace({ items: current.current.items.filter((item) => item.path !== target.path), message: 'Saved media removed.' });
      toast.success('Saved media removed');
      const loaded = await read({ token, explicit: true, lease });
      if (owns()) {
        try { await onChanged?.(opening.orderId, { status: 'delivered' }); }
        catch { if (owns() && loaded) replace({ message: 'Removal succeeded, but the run list could not refresh. Retry refresh only.' }); }
      }
    } catch (error) {
      const known = knownRejections.has(error.response?.status);
      updateDeliveryWorkspace(active, { savedMediaRemoval: known ? null : { ...target, status: 'unknown' } }, lease);
      if (!owns()) return;
      replace({ status: 'error', message: known
        ? 'Removal was rejected. The run may be closed or your permission may have changed. Refresh the saved list before trying again.'
        : 'Removal outcome is unknown. Review current delivery before doing more work. No removal will be retried automatically.' });
    } finally { releaseDeliveryAction(lease); }
  };
  const removal = getWork()?.savedMediaRemoval;
  const visible = state.opening === opening ? state : empty;
  return {
    ...visible, items: removal?.status === 'acknowledged' ? visible.items.filter((item) => item.path !== removal.path) : visible.items,
    message: removal?.status === 'unknown' && visible.status === 'ready'
      ? 'Removal outcome is unknown. Review current delivery before doing more work.'
      : removal?.status === 'acknowledged' && visible.status === 'ready'
        ? 'Removal succeeded. Review the current delivery to finish refreshing; do not repeat the removal.' : visible.message,
    offline: opening.binding?.source !== 'cloud',
    refresh, selectRemoval, confirmRemoval,
    cancelRemoval: () => { if (owns()) replace({ selected: null }); },
    busy: !!getDeliveryAction(getWork()),
    canRemove: enabled && opening.binding?.source === 'cloud' && state.opening === opening
      && canAct() && !removal && visible.status === 'ready' && !getDeliveryAction(getWork()),
    canRefresh: enabled && canAct() && !getDeliveryAction(getWork()),
    needsReview: !!removal,
    opening, owns, getToken,
  };
}
